/**
 * Normalizes user input into a valid URL
 * Examples:
 * - "heise.de" → "https://heise.de"
 * - "www.example.com" → "https://www.example.com"
 * - "http://example.com" → "http://example.com" (unchanged)
 */
export function normalizeUrl(input: string): string {
  // Trim whitespace
  input = input.trim();
  
  // If empty, return as-is
  if (!input) return input;
  
  // Check if protocol is already present
  if (input.match(/^https?:\/\//i)) {
    // Already has protocol, just validate and return
    try {
      return new URL(input).toString();
    } catch {
      return input; // Return as-is if invalid
    }
  }
  
  // Check for protocol-relative URLs
  if (input.startsWith('//')) {
    input = 'https:' + input;
  }
  // Add https:// if no protocol
  else if (!input.includes('://')) {
    input = 'https://' + input;
  }
  
  // Validate the URL
  try {
    return new URL(input).toString();
  } catch {
    // If still invalid, return with https://
    return 'https://' + input;
  }
}

/**
 * Checks if a URL is valid
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Extracts domain from URL for display
 */
export function getDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return url;
  }
}